import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions';
import { getSellerUrl } from '@/utils/urls';
import { isInitialRenderWithResultsAtom } from '@/pages/SearchPageSimplified/atoms/searchPageAtoms';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { safelyEscapeText } from '@/utils/safelyRender';
import { useAppSelector } from '@/redux/hooks';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useTrackAuctionHouseSuggestionClick } from '@/utils/amplitude/analytics';

type Props = {
    isLoadingSearchResults: boolean;
};

export const AuctionHouseSuggestionLink = ({ isLoadingSearchResults }: Props) => {
    const isInitialRenderWithResults = useAtomValue(isInitialRenderWithResultsAtom);

    const { houses } = useAppSelector(getSecondarySearchSuggestions);

    const trackAuctionHouseSuggestionClick = useTrackAuctionHouseSuggestionClick();

    const firstHouse = useMemo(() => {
        if (houses.length > 0) {
            return houses[0];
        }
        return null;
    }, [houses]);

    if (!firstHouse && !isInitialRenderWithResults) {
        return null;
    }
    return (
        // ADD THIS TO PREVENT LAYOUT SHIFT ON INITIAL RENDER
        <div
            className={cn('mb-sm', {
                'opacity-0': isInitialRenderWithResults || (!firstHouse && isLoadingSearchResults),
            })}
            data-testid="auctionHouseSuggestionLinkContainer"
        >
            <BodyPrimary>
                <FormattedMessage
                    id="searchResults.lookingFor"
                    values={{
                        suggestion: firstHouse ? (
                            <Link
                                aria-label={firstHouse.value}
                                href={getSellerUrl(firstHouse.houseId, firstHouse.value)}
                                key="house-link"
                                onClick={() => trackAuctionHouseSuggestionClick({ houseId: firstHouse.houseId })}
                            >
                                {safelyEscapeText(firstHouse.value).trim()}
                            </Link>
                        ) : (
                            <span key="house-suggestion" />
                        ),
                    }}
                />
            </BodyPrimary>
        </div>
    );
};
